import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getPageData } from 'utils/lib/strapi';
import { cmsCDNPath } from 'utils/helpers';
import {
  CANADA_SHELF,
  CANADA_SHELF_CARD,
  CANADA_SPOTLIGHT,
  CANADA_SPOTLIGHT_IMAGE,
  CANADA_SPOTLIGHT_LEGAL,
} from 'components/CanadaLandingPage/data/canadaContent';

/**
 * Strapi-backed content for the `/canada/landing-page` surface.
 *
 * Mirrors `useGetSeriesSpotlightVisibility` (the `private-landing-pages`
 * pattern) against a `canada-landing-pages` single entry, so marketing can
 * change the featured Canadian offering without a deploy. The content type
 * lives in the **startengine_cms** repo — until it exists, every field falls
 * back to the handoff copy in `canadaContent.tsx`, which is also what renders
 * if Strapi is unreachable.
 *
 * Expected Strapi fields (all optional):
 *   showTicker, showBelcoDisclosure, showFeaturedOffering, showOfferingShelf
 *   featuredOfferingName / Series / Tagline / Url / Image / Legal
 *   shelfEyebrow / shelfTitle / shelfDescription
 *   offeringMonogram / Sector / BadgeLabel / EligibilityNote / Description /
 *   Minimum / Structure / FillPercent / Disclosure / Href
 */

const SE_HOST = /(?:^|\.)startengine\.com$/i;

interface StrapiImage {
  data?: { attributes?: { url?: string; hash?: string; ext?: string } };
}

interface CanadaLandingAttributes {
  showTicker?: boolean;
  showBelcoDisclosure?: boolean;
  showFeaturedOffering?: boolean;
  showOfferingShelf?: boolean;
  featuredOfferingName?: string;
  featuredOfferingSeries?: string;
  featuredOfferingTagline?: string;
  featuredOfferingUrl?: string;
  featuredOfferingImage?: StrapiImage;
  featuredOfferingLegal?: string;
  shelfEyebrow?: string;
  shelfTitle?: string;
  shelfDescription?: string;
  offeringMonogram?: string;
  offeringSector?: string;
  offeringBadgeLabel?: string;
  offeringEligibilityNote?: string;
  offeringDescription?: string;
  offeringMinimum?: string;
  offeringStructure?: string;
  offeringFillPercent?: number;
  offeringDisclosure?: string;
  offeringHref?: string;
}

const getStrapiImageUrl = (imageData?: StrapiImage): string => {
  const attrs = imageData?.data?.attributes;
  if (!attrs) return '';
  return attrs.url || `${cmsCDNPath}/images/origin/${attrs.hash}${attrs.ext}`;
};

/**
 * Only same-site StartEngine http(s) URLs (or StartEngine-relative paths) are
 * allowed through. `javascript:` / `data:` schemes, off-domain hosts, and
 * malformed values fall back to the empty string so a hostile CMS value can
 * never reach `router.push` / `Link`. Mirrors the guard on the /private page.
 */
export const resolveCanadaOfferingHref = (raw?: string): string => {
  const value = raw?.trim() || '';
  if (!value) return '';
  try {
    const parsed = new URL(value, 'https://www.startengine.com');
    const isHttp = parsed.protocol === 'https:' || parsed.protocol === 'http:';
    if (isHttp && SE_HOST.test(parsed.hostname)) return value;
  } catch {
    return '';
  }
  return '';
};

const useCanadaLandingPage = ({ enabled = true } = {}) => {
  const queryClient = useQueryClient();
  const strapiContentId = queryClient.getQueryData(['strapiContentId']);

  const { data: strapiData, isLoading } = useQuery({
    queryKey: ['canadaLandingPageSettings', strapiContentId],
    queryFn: () =>
      getPageData({
        type: 'canada-landing-pages',
        preview: Boolean(strapiContentId),
        id: strapiContentId as string | undefined,
      }),
    staleTime: 5 * 60 * 1000,
    enabled,
  });

  const attrs: CanadaLandingAttributes =
    (strapiData as { attributes?: CanadaLandingAttributes } | null)?.attributes ?? {};

  const configuredImage = getStrapiImageUrl(attrs.featuredOfferingImage);

  return {
    isLoading,
    showTicker: attrs.showTicker ?? true,
    showBelcoDisclosure: attrs.showBelcoDisclosure ?? true,
    showFeaturedOffering: attrs.showFeaturedOffering ?? true,
    showOfferingShelf: attrs.showOfferingShelf ?? true,
    spotlight: {
      ...CANADA_SPOTLIGHT,
      name: attrs.featuredOfferingName?.trim() || CANADA_SPOTLIGHT.name,
      series: attrs.featuredOfferingSeries?.trim() || CANADA_SPOTLIGHT.series,
      desc: attrs.featuredOfferingTagline?.trim() || CANADA_SPOTLIGHT.desc,
    },
    spotlightImage: configuredImage || CANADA_SPOTLIGHT_IMAGE,
    spotlightLegal: attrs.featuredOfferingLegal?.trim() || CANADA_SPOTLIGHT_LEGAL,
    offeringHref: resolveCanadaOfferingHref(attrs.offeringHref),
    shelf: {
      eyebrow: attrs.shelfEyebrow?.trim() || CANADA_SHELF.eyebrow,
      title: attrs.shelfTitle?.trim() || CANADA_SHELF.title,
      desc: attrs.shelfDescription?.trim() || CANADA_SHELF.desc,
    },
    card: {
      companyKey: CANADA_SHELF_CARD.companyKey,
      name: attrs.featuredOfferingName?.trim() || CANADA_SHELF_CARD.name,
      mono: attrs.offeringMonogram?.trim() || CANADA_SHELF_CARD.mono,
      series: attrs.featuredOfferingSeries?.trim() || CANADA_SHELF_CARD.series,
      sector: attrs.offeringSector?.trim() || CANADA_SHELF_CARD.sector,
      badgeLabel: attrs.offeringBadgeLabel?.trim() || CANADA_SHELF_CARD.badgeLabel,
      metaPill: attrs.offeringEligibilityNote?.trim() || CANADA_SHELF_CARD.metaPill,
      desc: attrs.offeringDescription?.trim() || CANADA_SHELF_CARD.desc,
      minimum: attrs.offeringMinimum?.trim() || CANADA_SHELF_CARD.minimum,
      structure: attrs.offeringStructure?.trim() || CANADA_SHELF_CARD.structure,
      fillPercent:
        typeof attrs.offeringFillPercent === 'number'
          ? Math.min(100, Math.max(0, attrs.offeringFillPercent))
          : CANADA_SHELF_CARD.fillPercent,
      disclosure: attrs.offeringDisclosure?.trim() || CANADA_SHELF_CARD.disclosure,
    },
  };
};

export default useCanadaLandingPage;
